import {
  clubStaleTime,
  useClub,
  useRoutesByBrand,
  useStaff,
  useStaffChangeRole,
  useStaffCreate,
  useStaffEdit,
  useStaffSetClubAccessible,
} from "@gymflow/api";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { FormikProvider, useFormik } from "formik";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes } from "../../../../../hooks/usePortalRoutes";
import { useClubSettings } from "../../../../../providers";
import useGymflowModels from "../../../../../store";
import { Button, CloseIcon, SlideSideBar } from "../../../../atoms";
import { staffSidebarSchema } from "./constants";
import { StaffForm } from "./StaffForm";
import { StaffFormValues } from "./types";
import {
  getDefaultClub,
  getStaffFormInitialValues,
  normalizePicture,
} from "./utils/helpers";

export type StaffFormSidebarProps = {
  existingValues?: StaffFormValues;
  onClose: () => void;
  isOpen: boolean;
  editingStaffId?: string;
  setEditingStaffId: (id?: string) => void;
};

type PatchedStaffFields = Pick<
  StaffFormValues,
  "firstName" | "lastName" | "email" | "mobileNumber"
> & {
  picture?: ReturnType<typeof normalizePicture>;
};

export const StaffSidebar = ({
  existingValues,
  onClose,
  isOpen,
  editingStaffId,
  setEditingStaffId,
}: StaffFormSidebarProps) => {
  const { api } = useGymflowModels();
  const { brand } = usePortalRoutes();
  const settings = useClubSettings();
  const clubId = settings.clubId;
  const { data: club } = useClub({ clubId, api }, { staleTime: clubStaleTime });
  const { t } = useTranslation();
  const { data: clubRoutes } = useRoutesByBrand({
    clubId: club?.id?.toString(),
    brand,
    page: 0,
    limit: 1,
  });

  const editStaff = useStaffEdit({ api });
  const createStaff = useStaffCreate({ api });
  const setClubsAccessibleStaff = useStaffSetClubAccessible({ api });
  const changeRoleMutation = useStaffChangeRole({ api });
  const { userId: loggedInId, logout } = useContext(AuthenticationContext);
  const { data: editing } = useStaff({
    api,
    staffId: editingStaffId ?? undefined,
  });

  const initialValues = useMemo(
    () =>
      getStaffFormInitialValues({
        existingValues,
        club,
        clubRoutes: clubRoutes?.results,
      }),
    [club, clubRoutes?.results, existingValues],
  );

  const isEdit = Boolean(initialValues?.id);
  const showEditingSelfRoleWarning = loggedInId === existingValues?.id;

  const handleSubmit = useCallback(
    async (values: StaffFormValues) => {
      if (editing) {
        const { clubAccessibleList, role, id, picture, ...staffFields } =
          values;
        const normalizedNew = normalizePicture(picture);
        const normalizedOld = normalizePicture(editing.picture);

        const patchedFields: PatchedStaffFields = {
          ...staffFields,
        };
        if (
          normalizedNew?.blob !== normalizedOld?.blob ||
          normalizedNew?.name !== normalizedOld?.name
        ) {
          patchedFields.picture = normalizedNew;
        }

        if (Object.keys(staffFields) && id) {
          await editStaff.mutateAsync({
            staffId: id,
            patchedFields,
          });
        }
        if (clubAccessibleList && id) {
          setClubsAccessibleStaff.mutateAsync({
            staffId: id,
            accessibleClubs: clubAccessibleList.map((c) => Number(c.value.id)),
          });
        }
        if (role && role.value !== editing.role && id) {
          await changeRoleMutation.mutateAsync({
            clubId: settings.clubId,
            staffId: id,
            role: role.value,
          });

          if (values.id === loggedInId) {
            logout();
          }
        }
      } else {
        const { clubAccessibleList, role, picture, ...staffFields } = values;
        const patchedFields = {
          ...staffFields,
          picture: normalizePicture(picture),
        };

        const {
          data: { id },
        } = await createStaff.mutateAsync(patchedFields);
        setClubsAccessibleStaff.mutateAsync({
          staffId: id,
          accessibleClubs: clubAccessibleList.map((c) => Number(c.value.id)),
        });
        if (role?.value && role.value !== "MANAGER") {
          await changeRoleMutation.mutateAsync({
            clubId: settings.clubId,
            staffId: id,
            role: role.value,
          });
        }
      }

      setEditingStaffId(undefined);
      onClose();
    },
    [
      changeRoleMutation,
      createStaff,
      editStaff,
      editing,
      loggedInId,
      logout,
      onClose,
      setClubsAccessibleStaff,
      setEditingStaffId,
      settings.clubId,
    ],
  );

  const defaultClub = useMemo(
    () => getDefaultClub(club, clubRoutes?.results),
    [club, clubRoutes?.results],
  );

  const formik = useFormik<StaffFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: toFormikValidationSchema(staffSidebarSchema(defaultClub)),
    onSubmit: handleSubmit,
  });

  const { resetForm } = formik;

  useEffect(() => {
    if (isOpen) return;
    resetForm();
  }, [isOpen, resetForm]);

  return (
    <SlideSideBar
      isOpen={isOpen}
      hide={onClose}
      className="!fixed !right-0 !top-0 !m-0 !h-screen w-[32rem] max-w-full"
      overlayClassName="!fixed"
    >
      <FormikProvider value={formik}>
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-8">
            <div className="mb-1 flex flex-row items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">
                {t("page.settings.tab.staff.addStaff.title")}
              </div>
              <div className="cursor-pointer" onClick={onClose}>
                <CloseIcon />
              </div>
            </div>
            <div className="pr-16 text-base text-gray-600">
              {t("page.settings.tab.staff.addStaff.description")}
            </div>
            <StaffForm
              showEditingSelfRoleWarning={showEditingSelfRoleWarning}
              formik={formik}
            />
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 p-4">
            <Button className="w-full" intent="default" onClick={onClose}>
              {t("page.settings.tab.staff.addStaff.cancel")}
            </Button>
            <Button
              intent="secondary"
              className="w-full"
              onClick={async () => {
                await formik.submitForm();
              }}
              disabled={isEdit && !formik.dirty}
            >
              {isEdit
                ? t("page.settings.tab.staff.addStaff.save")
                : t("page.settings.tab.staff.addStaff.add")}
            </Button>
          </div>
        </div>
      </FormikProvider>
    </SlideSideBar>
  );
};
